import LearnLayout from "@/components/LearnLayout";
import { FeatureStack } from "@/components/learn/FeatureStack";
import {
  Upload, Check, Trophy, ChevronRight, TrendingUp,
} from "lucide-react";

const STEPS = [
  { n: "01", title: "Submit Application", desc: "Fill out the sponsorship application in four quick and straight forward steps." },
  { n: "02", title: "Get Sponsored", desc: "We will review and prepare a sponsorship deal that fits your racing profile and tournaments." },
  { n: "03", title: "Keep Racing", desc: "You can chat or request upgrades and reviews at any time as long as you are racing and growing." },
  { n: "04", title: "The SRC Academy", desc: "Launching in 2027, limited seats every year offered to the top drivers in the Pro sponsorship tier to take their driving from sim to real." },
];

function ApplicationWizardBody() {
  const wizard = [
    { n: "01", label: "Platform", state: "done" },
    { n: "02", label: "Details", state: "done" },
    { n: "03", label: "Livery", state: "active" },
    { n: "04", label: "Review", state: "todo" },
  ];
  return (
    <div>
      <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
        Everything the sponsorship team needs, structured and focused. No long forms or spending hours on proposals.
      </p>
      <div className="mt-8 rounded-xl border border-border/70 bg-background/60 p-5 shadow-xl backdrop-blur">
        <div className="flex items-center gap-1.5 sm:gap-3">
          {wizard.map((s, i) => (
            <div key={s.n} className="flex flex-1 items-center gap-2">
              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold ${
                s.state === "done" ? "bg-primary text-primary-foreground" :
                s.state === "active" ? "border-2 border-primary bg-primary/10 text-primary" :
                "border border-border text-muted-foreground/60"
              }`}>
                {s.state === "done" ? <Check className="h-4 w-4" /> : s.n}
              </div>
              <span className={`hidden text-[11px] font-semibold uppercase tracking-widest sm:inline ${
                s.state === "todo" ? "text-muted-foreground/60" : "text-foreground/90"
              }`}>{s.label}</span>
              {i < wizard.length - 1 && (
                <div className={`ml-1 h-px flex-1 ${s.state === "done" ? "bg-primary/60" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-5 border-t border-border pt-4">
          <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary/80">Livery / Branding</p>
          {/* Mock of the real "Upload Livery File" field used in the application */}
          <div className="mt-3 rounded-lg border-2 border-dashed border-primary/40 bg-primary/[0.04] px-4 py-5 text-center">
            <Upload className="mx-auto h-7 w-7 text-primary/80" />
            <p className="mt-2 text-sm font-semibold text-foreground">Upload Livery File</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Images or TradingPaints files</p>
          </div>
          {/* Additional info entered — "describe your livery plans" */}
          <div className="mt-3">
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Describe your livery plans</p>
            <div className="mt-1.5 rounded-md border border-border bg-background/70 px-3 py-2 text-xs leading-relaxed text-foreground/70">
              I can add SRC and MOZA logos on the side pods and the nose. How does that sound?
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DirectChatBody() {
  return (
    <div>
      <div className="space-y-3">
        {/* You (driver) — right */}
        <div className="flex items-start justify-end gap-2">
          <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-secondary/80 px-3 py-2 text-xs text-foreground/70">
            Got a team together for the 24 Hours of Daytona — looking for some backing to support the race.
          </div>
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-foreground/70">You</div>
        </div>
        {/* SRC — left */}
        <div className="flex items-start gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary">SRC</div>
          <div className="max-w-[80%] rounded-2xl rounded-tl-sm border border-primary/30 bg-primary/[0.06] px-3 py-2 text-xs text-foreground/70">
            Love it — that&apos;s a big one. Send the team and car details across and we&apos;ll review it for you.
          </div>
        </div>
      </div>
      <p className="mt-6 text-[0.963rem] leading-relaxed text-foreground/80">
        No email tag, no waiting weeks between replies.
      </p>
    </div>
  );
}

function TiersBody() {
  const tiers = [
    { n: "01", title: "Grassroots", desc: "Exclusive discounts and referral bonuses" },
    { n: "02", title: "Rising", desc: "Higher discounts, upgrades, and referral earnings" },
    { n: "03", title: "Pro", desc: "Get paid to race" },
    { n: "04", title: "SRC Academy", desc: "Limited seat elite level for the most accomplished sim racers.", academy: true },
  ];
  return (
    <div>
      <p className="max-w-xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
        Four tiers that are designed to offer sponsorships that fit sim racers at every level, from grassroots to support a transition to a real car.
      </p>
      <div className="mt-5 space-y-2.5">
        {tiers.map((t) => (
          <div
            key={t.n}
            className={`flex items-center gap-4 rounded-lg border bg-background/60 px-4 py-3 transition-colors duration-300 ${
              t.academy ? "border-[#FFC906]/40 hover:border-[#FFC906]/70" : "border-border hover:border-primary/40"
            }`}
          >
            <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md font-mono text-xs font-bold tabular-nums ${
              t.academy
                ? "bg-[#FFC906] text-black shadow-[0_0_14px_-3px_#FFC906]"
                : "bg-primary text-primary-foreground shadow-[0_0_14px_-3px_hsl(var(--primary)/0.8)]"
            }`}>
              {t.n}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`font-display text-base font-bold uppercase leading-tight ${t.academy ? "text-[#FFC906]" : ""}`}>{t.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t.desc}</p>
            </div>
            <ChevronRight className={`h-4 w-4 flex-shrink-0 ${t.academy ? "text-[#FFC906]/60" : "text-muted-foreground/50"}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function GrowthBody() {
  const steps = [
    { label: "Grassroots", h: "h-10" },
    { label: "Rising", h: "h-16" },
    { label: "Pro", h: "h-24" },
  ];
  return (
    <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
      <div>
        <p className="max-w-2xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
          Your sponsorship deal grows as you do. Start with gear discounts and referrals, then apply to move up as your results build. Each step up is reviewed on merit and your sponsorship package grows with it.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {["Apply to move up", "Reviewed on performance", "Bigger deal each tier"].map((t) => (
            <span key={t} className="rounded-full border border-border bg-background/70 px-3 py-1 text-xs text-foreground/80">
              <TrendingUp className="mr-1 inline h-3 w-3 text-primary" />{t}
            </span>
          ))}
        </div>
      </div>
      {/* Growth-over-time mock */}
      <div className="mx-auto flex items-end gap-3">
        {steps.map((s, i) => (
          <div key={s.label} className="flex flex-col items-center gap-2">
            <div className={`w-12 ${s.h} rounded-t-md ${i === steps.length - 1 ? "bg-primary" : "bg-primary/40"}`} />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  {
    pos: "01",
    eyebrow: "// APPLY",
    title: "Guided 4-step application",
    summary: "A short, structured application, no long forms or proposals needed.",
    content: <ApplicationWizardBody />,
  },
  {
    pos: "02",
    eyebrow: "// CHAT",
    title: "Talk to the team directly",
    summary: "Chat with the sponsorship team inside the portal.",
    content: <DirectChatBody />,
  },
  {
    pos: "03",
    eyebrow: "// TIERS",
    title: "Four tiers to fit every sim racer",
    summary: "Grassroots, Rising, Pro, and the SRC Academy: you can get support at every level.",
    content: <TiersBody />,
  },
  {
    pos: "04",
    eyebrow: "// GROWTH",
    title: "Grow over time",
    summary: "Apply to move up and your deal grows with your results.",
    content: <GrowthBody />,
  },
];

export default function LearnSponsorship() {
  return (
    <LearnLayout
      testId="learn-sponsorship"
      kicker=""
      title={<><span className="block text-[0.75em]">Get Sponsored,</span>Race like the <span className="text-gradient-primary-soft">Pros</span></>}
      sub="From grassroots sponsorships to getting paid to race up to getting into a real race car, the SRC Sponsorship portal launches your sim racing career in four quick steps."
      heroImage="/banners/src-sponsor-banner.webp"
      heroBgSize="contain"
      heroBgPosition="right center"
      bodyBg="#eceef1"
    >
      <section className="pl-12 sm:pl-14">
        <div className="relative z-20 inline-block -skew-x-[11deg] bg-primary px-4 py-2 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.65)] sm:px-5 sm:py-2.5">
          <h2 className="skew-x-[11deg] font-display text-[1.94rem] font-bold italic uppercase leading-none text-white sm:text-[2.43rem]">
            Connecting racers with Sponsorships that grow
          </h2>
        </div>
        <div className="relative z-10 -mt-3">
          <FeatureStack items={FEATURES} testId="sponsorship-feature-stack" tabBadge largeBody />
        </div>
      </section>

      <section
        className="relative mt-20 overflow-hidden bg-carbon"
        style={{ width: "100vw", marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      >
        <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-[#FFC906]/60" />
        <Trophy aria-hidden className="pointer-events-none absolute -right-8 top-6 h-56 w-56 text-[#FFC906]/[0.06]" strokeWidth={1} />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase text-white">
            <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-[#FFC906]" />
            The top of the ladder: <span className="text-[#FFC906]">SRC Academy</span>
          </h2>
          <p className="mt-3 max-w-2xl text-[0.963rem] text-foreground/70">
            An elite ultra-limited seat program for the most accomplished sim racers - exclusively on the SRC Platform.
          </p>

          {/* Tier ladder — text-only progression, Academy is the pinnacle */}
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-lg font-bold uppercase">
            {["Grassroots", "Rising", "Pro"].map((t) => (
              <span key={t} className="flex items-center gap-3">
                <span className="text-foreground/70">{t}</span>
                <span aria-hidden className="text-foreground/40">›</span>
              </span>
            ))}
            <span className="text-[#FFC906]">SRC Academy</span>
          </div>

          <p className="mt-8 max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/85">
            The SRC Academy is a sim-to-real academy launching in 2027 and built to support and prepare the very best sim racers on the SRC Platform for the jump into a real car. Prep goes well beyond coaching: it includes <strong className="text-foreground">real seat time, on-track practice sessions, and test days</strong>.
          </p>
          <p className="mt-4 max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/85">
            This tier isn&apos;t about the size of your audience or following. It&apos;s based on performance alone: <strong className="text-foreground">consistency, race craft, pace, and good sportsmanship</strong>.
          </p>
          <p className="mt-4 max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/85">
            With an <strong className="text-foreground">ultra-limited seat capacity</strong>, it is designed to support the highest performing sim racers who have proven themselves on the SRC Platform.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { t: "Judged on performance", d: "Consistency, race craft, pace, and sportsmanship, not follower counts or audience size." },
              { t: "Real seat time", d: "Not just coaching; real-car seat time, practice sessions, and test days to get you genuinely track-ready." },
              { t: "Earned by Top Racers", d: "The SRC team elevates only the highest performing racers from the Pro tier to a limited number of seats every season." },
            ].map((f) => (
              <div key={f.t} className="rounded-lg border border-[#FFC906]/25 bg-background/40 p-4">
                <p className="font-display text-sm font-bold uppercase text-[#FFC906]">{f.t}</p>
                <p className="mt-2 text-[0.963rem] leading-relaxed text-foreground/75">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-20">
        <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase" style={{ color: "#0b0b0d" }}>
          <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
          How Sponsorships Work
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-4">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-lg border border-border bg-card p-6">
              <span className="font-display text-5xl font-bold text-primary/70">{s.n}</span>
              <h3 className="mt-2 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-base" style={{ color: "#c7c8cf" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </LearnLayout>
  );
}
